import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { enableWebPush, isPushSupported } from "../lib/webpush";
import { toast } from "sonner";

/**
 * FirstLoginNotifPrompt
 * ---------------------
 * A one-time modal that fires the very first time a user signs in on this
 * browser.  Offers to enable push notifications so critical events (task
 * overdue, arrival alerts, C-Form 24h ticks) reach the founder / GEHM /
 * PCWM even when the tab is closed.
 *
 * Marker is stored in `localStorage` keyed by user id, so the same person
 * doesn't get bothered again on subsequent logins on the same device — but a
 * fresh device / incognito session will re-prompt (that's expected).
 */
export default function FirstLoginNotifPrompt({ userId }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const key = `aii-notif-prompt:${userId}`;
    if (localStorage.getItem(key) === "1") return;
    if (!isPushSupported()) { localStorage.setItem(key, "1"); return; }
    if (typeof Notification !== "undefined" && Notification.permission !== "default") {
      // Already granted or explicitly denied — nothing for us to do.
      localStorage.setItem(key, "1");
      return;
    }
    // Delay a moment so the user isn't blasted the instant they land.
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [userId]);

  const dismiss = () => {
    if (userId) localStorage.setItem(`aii-notif-prompt:${userId}`, "1");
    setOpen(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await enableWebPush();
      toast.success("Push notifications enabled — you'll get alerts even when the tab is closed");
    } catch (e) { toast.error(e?.message || "Couldn't enable notifications"); }
    finally { setBusy(false); dismiss(); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
         data-testid="first-login-notif-prompt" onClick={dismiss}>
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[color:var(--aii-green-tint)] flex items-center justify-center flex-shrink-0">
              <Bell className="text-brand" size={20}/>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold">Turn on push notifications?</h3>
              <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">One-time setup · takes 2 seconds</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-[color:var(--aii-muted)] hover:text-red-600 p-1"><X size={16}/></button>
        </div>
        <div className="p-5 text-sm text-[color:var(--aii-text-2)] space-y-2">
          <p>Aii Group can nudge you the moment something needs attention — overdue tasks, new arrivals, C-Form 24h alerts — even when this tab is closed.</p>
          <p className="text-[color:var(--aii-muted)] text-xs">You can flip this off any time from your profile settings.</p>
        </div>
        <div className="p-4 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
          <button data-testid="first-login-notif-skip" onClick={dismiss}
                  className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50 font-semibold">
            Not now
          </button>
          <button data-testid="first-login-notif-enable" onClick={enable} disabled={busy}
                  className="px-4 py-2 text-sm bg-brand text-white rounded-md hover:bg-brand-dark disabled:opacity-50 font-semibold flex items-center gap-1.5">
            <Bell size={13}/> {busy ? "Requesting…" : "Enable notifications"}
          </button>
        </div>
      </div>
    </div>
  );
}
